import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "utils/dayjs";
import type {
  DateRangeFilterLabels,
  DateRangePickerLabels,
  DateRangePreset,
  DateRangeValue,
} from "@outpave/ds";

/** Epoch-millisecond query bounds. Either end may be open. */
export interface DateRangeBounds {
  startDate?: number;
  endDate?: number;
}

/**
 * Our preset ids and their translation keys.
 *
 * The package ships four defaults (7/30/90/YTD) with English labels baked in,
 * so we supply our own: translated, and carrying the "Today" and "Last 60 days"
 * choices the package has no equivalent for. Ids are ours to pick because
 * `resolveDateRange` below is what gives them meaning.
 */
const PRESETS: ReadonlyArray<{ id: string; labelKey: string }> = [
  { id: "today", labelKey: "Today" },
  { id: "7d", labelKey: "Last 7 days" },
  { id: "30d", labelKey: "Last 30 days" },
  { id: "60d", labelKey: "Last 60 days" },
  { id: "90d", labelKey: "Last 90 days" },
  { id: "year", labelKey: "This year" },
];

export function useDateRangePresets(): DateRangePreset[] {
  const { t } = useTranslation();
  return useMemo(
    () => PRESETS.map(({ id, labelKey }) => ({ id, label: t(labelKey) })),
    [t],
  );
}

/** The strings the filter renders itself. English defaults, so translate them. */
export function useDateRangeFilterLabels(): DateRangeFilterLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      from: t("From"),
      to: t("To"),
      cancel: t("Cancel"),
      apply: t("Apply"),
      allTime: t("All time"),
      customRange: t("Custom range"),
    }),
    [t],
  );
}

/** The strings the range picker and its calendar render. English defaults, so translate them. */
export function useDateRangePickerLabels(): DateRangePickerLabels {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      from: (format) => `${t("From")} (${format})`,
      to: (format) => `${t("To")} (${format})`,
      separator: t("to"),
      start: t("Start Date"),
      end: t("End Date"),
      cancel: t("Cancel"),
      apply: t("Apply"),
      clear: t("Clear"),
      customRange: t("Custom range"),
      toolbarTitle: t("Select date range"),
      toolbarEmpty: t("Select dates"),
      presetPlaceholder: t("Select preset"),
    }),
    [t],
  );
}

// "Last 7 days" counts today as day one, so it reaches back six. Carried over
// verbatim from the deleted DateFilterMenu: an off-by-one here silently shifts
// every filtered result by a day.
const daysAgo = (n: number) =>
  dayjs()
    .subtract(n - 1, "day")
    .startOf("day");

const endOfToday = () => dayjs().endOf("day");

const PRESET_RANGES: Readonly<
  Record<string, () => { from: Dayjs; to: Dayjs }>
> = {
  today: () => ({ from: dayjs().startOf("day"), to: endOfToday() }),
  "7d": () => ({ from: daysAgo(7), to: endOfToday() }),
  "30d": () => ({ from: daysAgo(30), to: endOfToday() }),
  "60d": () => ({ from: daysAgo(60), to: endOfToday() }),
  "90d": () => ({ from: daysAgo(90), to: endOfToday() }),
  year: () => ({ from: dayjs().startOf("year"), to: endOfToday() }),
};

/**
 * Resolve a filter value to query bounds.
 *
 * The package attaches no date math to a preset by design: it reports an id and
 * the app decides what the id covers. This is that decision, kept in one place
 * so the transactions and receipt queries cannot drift apart on what a preset
 * means. A preset is resolved at call time, not at pick time, so a long-lived
 * page does not keep filtering against the day it was opened.
 */
export function resolveDateRange(value?: DateRangeValue): DateRangeBounds {
  if (!value) return {};

  const preset = value.presetId ? PRESET_RANGES[value.presetId] : undefined;
  if (preset) {
    const { from, to } = preset();
    return { startDate: from.valueOf(), endDate: to.valueOf() };
  }

  // Custom range, or a preset id we no longer ship. Bound whichever end is set.
  return {
    startDate: value.from ? value.from.startOf("day").valueOf() : undefined,
    endDate: value.to ? value.to.endOf("day").valueOf() : undefined,
  };
}
